// Crystal.js
import React from 'react';
import { HelpCircle } from 'lucide-react';

const patterns = {
  solid: () => null,
  stripes: (color) => ({
    backgroundImage: `repeating-linear-gradient(
      45deg,
      rgba(255, 255, 255, 1),
      rgba(255, 255, 255, 1) 3px,
      transparent 3px,
      transparent 10px
    )`
  }),
  dots: (color) => ({
    backgroundImage: `radial-gradient(
      circle at 4px 4px,
      rgba(255, 255, 255, 1) 2px,
      transparent 2px
    )`,
    backgroundSize: '10px 10px'
  }),
  grid: (color) => ({
    backgroundImage: `linear-gradient(
        rgba(255, 255, 255, 0.4) 2px,
        transparent 2px,
        transparent 10px
      ),
      linear-gradient(
        90deg,
        rgba(255, 255, 255, 0.4) 2px,
        transparent 2px,
        transparent 10px
      )`,
    backgroundSize: '10px 10px'
  }),
  diagonalCheckers: (color) => ({
    backgroundImage: `repeating-linear-gradient(
      45deg,
      rgba(255, 255, 255, 0.8),
      rgba(255, 255, 255, 0.8) 10px,
      transparent 10px,
      transparent 20px
    )`
  }),
  waves: (color) => ({
    backgroundImage: `repeating-radial-gradient(
      circle,
      rgba(255, 255, 255, 0.7),
      rgba(255, 255, 255, 0.7) 5px,
      transparent 5px,
      transparent 15px
    )`,
    backgroundSize: '50px 50px'
  }),
  triangles: (color) => ({
    backgroundImage: `repeating-linear-gradient(
      60deg,
      rgba(255, 255, 255, 0.7) 0px,
      rgba(255, 255, 255, 0.7) 10px,
      transparent 10px,
      transparent 20px
    ),
    repeating-linear-gradient(
      -60deg,
      rgba(255, 255, 255, 0.7) 0px,
      rgba(255, 255, 255, 0.7) 10px,
      transparent 10px,
      transparent 20px
    )`
  }),
};

const Crystal = ({ 
  color, 
  pattern = 'solid',
  size = 1,
  position, 
  config,
  isAnimating,
  isCombining,
  combineProgress = 1,
  totalPositions = 4,
  isRevealed
}) => {
  //console.log('Crystal render:', { color, position, isRevealed });
  const scale = config.scale || 1;
  const getStyle = () => {
    const baseHeight = (size / totalPositions) * config.containerHeight;
    const baseStyle = {
      position: 'absolute',
      left: 2,
      right: 2,
      height: `${baseHeight}px`,
      top: (position / totalPositions) * config.containerHeight,
      transition: isCombining ? 'height 0.15s ease-out' : 'all 0.15s ease-out',
      zIndex: isCombining ? 45 : Math.floor(position),
      opacity: isAnimating ? 0 : 1,
      backgroundColor: isRevealed ? color : '#9CA3AF',
      borderRadius: '0.375rem',
      ...(isRevealed && pattern !== 'solid' && patterns[pattern] ? patterns[pattern](color) : {})
    };
  
    if (isRevealed && pattern !== 'solid' && patterns[pattern]) {
      const patternStyle = patterns[pattern](color);
      return {
        ...baseStyle,
        ...patternStyle,
        backgroundBlendMode: 'overlay',
        backgroundPosition: 'center center'
      };
    }
  
    return baseStyle;
  };

  return (
    <div className="rounded-md" style={getStyle()}>
      <div
        className="absolute inset-[2px] rounded-md"
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.2)',
          mixBlendMode: 'overlay'
        }}
      />
      
      {!isRevealed && (
        <div className="absolute inset-0 flex items-center justify-center text-white">
          <HelpCircle style={{ width: `${24 * scale}px`, height: `${24 * scale}px` }} />
        </div>
      )}
      
      {size > 1 && (
        <div
          className="absolute inset-0 flex items-center justify-center text-white font-bold select-none shadow-inner"
          style={{ fontSize: `${Math.max(10, Math.round(18 * scale))}px` }}
        >
          {size}
        </div>
      )}
    </div>
  );
};

export default Crystal;